// KAN-1342: доменные типы единого чата (личка и группы — одна модель, как peer в Telegram).

export const CONVERSATION_TYPES = ["direct", "team", "organization", "temporary", "custom"] as const;
export type ConversationType = (typeof CONVERSATION_TYPES)[number];

export const MESSAGE_KINDS = ["text", "lfg", "scrimInvite", "system"] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

// KAN-1525: round — видеосообщение («кружок»).
export const ATTACHMENT_TYPES = ["image", "video", "voice", "file", "round"] as const;
export type AttachmentType = (typeof ATTACHMENT_TYPES)[number];

export interface ConversationLastMessage {
  id: string;
  senderId: string | null;
  senderDisplayName: string | null;
  preview: string;
  sentAtUtc: string;
  isDeleted: boolean;
}

export interface Conversation {
  id: string;
  type: ConversationType;
  title: string;
  avatarUrl: string | null;
  /** Собеседник лички; null у групп. */
  peerUserId: string | null;
  memberCount: number;
  lastMessage: ConversationLastMessage | null;
  unreadCount: number;
  isArchived: boolean;
  notificationsMuted: boolean;
  pinnedAtUtc: string | null;
  chatListId: string | null;
  isOwner: boolean;
  blockedByMe: boolean;
  blockedMe: boolean;
  membersCanInvite: boolean;
  mediaAllowed: boolean;
  slowMode: boolean;
  expiresAtUtc: string | null;
}

export interface ChatAttachment {
  id: string;
  type: AttachmentType;
  url: string;
  thumbnailUrl: string | null;
  fileName: string | null;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  durationSec: number | null;
}

export interface ChatReplyPreview {
  id: string;
  senderDisplayName: string | null;
  preview: string | null;
  isDeleted: boolean;
  senderAvatarUrl: string | null;
}

/** KAN-1329: реакции одного эмодзи; «моя» определяется по userIds (сервер отдаёт до 50). */
export interface ChatReaction {
  emoji: string;
  count: number;
  userIds: string[];
  names: string[];
  /** Аватары в том же порядке, что names (до 3); null — аватара нет. */
  avatars: (string | null)[];
  /** KAN-1483: когда смотрящий поставил эту реакцию (ISO); нет — реакция не его. По нему двойной клик снимает последнюю. */
  myAtUtc?: string | null;
}

/** KAN-1333: личный список чатов в рэйле; kind — вкладка, где он живёт. */
export interface ChatList {
  id: string;
  kind: "direct" | "groups";
  title: string;
}

/** KAN-1339: счётчики вкладок инфо-панели. */
export interface ChatMediaCounts {
  photos: number;
  videos: number;
  files: number;
  voices: number;
  links: number;
}

// KAN-1572: round — кружок; приходит во вкладке «Голосовые» (отдельной вкладки у кружков нет).
export type ChatMediaKind = "photo" | "video" | "file" | "voice" | "round" | "link";

/** KAN-1339: элемент вкладки инфо-панели (вложение или ссылка из текста). */
export interface ChatMediaItem {
  messageId: string;
  attachmentId: string | null;
  kind: ChatMediaKind;
  url: string;
  thumbnailUrl: string | null;
  fileName: string | null;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  durationSec: number | null;
  /** У ссылки — её домен. */
  title: string | null;
  senderDisplayName: string | null;
  sentAtUtc: string;
}

/** KAN-1330: закреплённое сообщение — для полосы под шапкой и панели. */
export interface ChatPin {
  messageId: string;
  senderDisplayName: string | null;
  preview: string;
  pinnedAtUtc: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string | null;
  senderDisplayName: string | null;
  senderAvatarUrl: string | null;
  kind: MessageKind;
  body: string;
  payloadJson: string | null;
  attachments: ChatAttachment[];
  sentAtUtc: string;
  editedAtUtc: string | null;
  replyTo: ChatReplyPreview | null;
  forwardedFromName: string | null;
  isDeleted: boolean;
  isDelivered: boolean;
  isRead: boolean;
  reactions: ChatReaction[];
  /** KAN-1465: ключ отправки (временный id оптимистичной копии) — сервер отдаёт его только автору. */
  clientKey?: string | null;
  /** Только на клиенте: оптимистичное сообщение ещё не подтверждено сервером. */
  isPending?: boolean;
  isFailed?: boolean;
}

export interface ChatMessagesPage {
  messages: ChatMessage[];
  hasMore: boolean;
}

export interface ConversationMember {
  userId: string;
  /** KAN-1578: ник для упоминаний «@ник». */
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  joinedAtUtc: string;
  isOwner: boolean;
  mutedUntilUtc: string | null;
}

/** KAN-1336: снимок LFG/LFT-объявления в карточке чата (payloadJson сообщения вида lfg). */
export interface LfgCard {
  postId: string;
  type: "lfg" | "lft";
  authorId: string;
  authorName: string;
  authorAvatarUrl: string | null;
  teamName: string | null;
  role: string;
  roles: string[];
  formats: string[];
  minRank: string | null;
  maxRank: string | null;
  description: string;
}

/** KAN-1337: команда в приглашении на скрим. */
export interface ScrimTeamOption {
  id: string;
  name: string;
  tag: string;
  logoUrl: string | null;
}

export interface ScrimOptions {
  myTeams: ScrimTeamOption[];
  peerTeams: ScrimTeamOption[];
}

/** Снимок приглашения на скрим (payloadJson сообщения вида scrimInvite). */
export interface ScrimCard {
  scrimId: string;
  from: ScrimTeamOption;
  to: ScrimTeamOption;
  /** Предложенное время начала (ISO, UTC); у старых карточек нет. */
  scheduledAt: string | null;
  /** Серия: 1, 3 или 5; у старых карточек нет. */
  bestOf: number | null;
  /** Все предложенные начала (ISO, UTC); у старых карточек — только scheduledAt. */
  dates: string[];
}
